import { UploadFile, Warning } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { FileUploadProgress } from "components/upload/FileUploadProgress";
import { useIdentity } from "contexts/identity-context";
import { submitClipGeometryFile } from "data/mutations-common";
import { useS3FileUploadDropper } from "pages/geoscape-admin/custom-datasets/custom-hook/customHooks";
import { zoomToFeatureCollection } from "pages/geoscape-data/explorer/utils";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { GeoJSONFeatureCollection } from "../../models";
import { BoundaryToolProps } from "./define-boundary";

type UploadStates = "ready" | "uploading" | "processing" | "complete";

const uploadBucket =
  import.meta.env.VITE_ENVIRONMENT === "production"
    ? "prod-hub-clip-storage"
    : "preprod-hub-clip-storage";

export const UploadComponent = (
  props: ClipConfigProps & ClipStepsControlProps & BoundaryToolProps
) => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const [uploadState, setUploadState] = useState<UploadStates>("ready");
  const [featureExtractCount, setFeatureExtractCount] = useState(0);
  const [warnings, setWarnings] = useState<string[]>([]);

  const {
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    removeFile,
    getRootProps,
    getInputProps,
    uploadFiles,
  } = useS3FileUploadDropper({
    maxSize: 10 * 1024 * 1024, //10mb
    maxFiles: 1,
    prefixFileName: `${identityId}/raw_input/`,
    acceptExtension: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
  });

  useEffect(() => {
    if (files.length > 0 && !isUploading) {
      uploadFiles(uploadBucket);
      setUploadState("uploading");
    }
  }, [files]);

  const { enqueueMutationFailed } = useCustomSnackbars();

  const submitFileMutation = useMutation({
      mutationFn: (s3Key: string) => submitClipGeometryFile(s3Key),
      onSuccess: (fc: GeoJSONFeatureCollection) => {
          setUploadState("complete");
          setWarnings(fc.messages ?? []);
          setFeatureExtractCount(fc.features.length);
          props.addOrUpdateFeatures(fc.features);
          fc.features.map((f) => {
            props.draw.add(f);
          });
          zoomToFeatureCollection(props.mapRef, fc);
        },
      onError: (error: Error) => {
          setUploadState("complete");
          enqueueMutationFailed(error.toString());
        },
    });
  const cancelUpload = (path: string) => {
    removeFile(path);
    setUploadState("ready");
  };
  useEffect(() => {
    if (haveUploaded === true) {
      setUploadState("processing");
      const uploadedFileKey = `${identityId}/raw_input/${files[0].name}`;
      submitFileMutation.mutate(uploadedFileKey);
      files.forEach((x) => {
        removeFile(x.name);
      });
    }
  }, [haveUploaded]);

  return (
    <Box>
      {uploadState === "ready" && (
        <>
          <Box
            style={{
              border: "dotted",
              borderColor: "grey",
              margin: "8px",
              padding: "8px",
              textAlign: "center",
            }}
            {...getRootProps({ className: "dropzone" })}
          >
            <input disabled={true} {...getInputProps()} />
            <UploadFile color="info" />
            <Typography>Click to upload or drag and drop file</Typography>
            <Typography variant="body2">*Max file size is 10 MB</Typography>
            <Typography></Typography>
          </Box>
        </>
      )}

      {uploadState === "uploading" && (
        <FileUploadProgress
          uploadProgress={uploadProgress}
          file={files[0]}
          onRemove={cancelUpload}
          isUploading={isUploading}
          haveUploaded={haveUploaded}
        />
      )}
      {uploadState === "processing" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",

            marginTop: "16px",
          }}
        >
          <Typography variant="h6">Processing file</Typography>
          <CircularProgress
            sx={{
              marginTop: "16px",
            }}
          />
        </Box>
      )}
      {uploadState === "complete" &&
        warnings.length > 0 &&
        warnings.map((x) => (
          <Alert
            sx={{
              marginTop: "16px",
            }}
            icon={<Warning />}
            color="warning"
          >
            {x}
          </Alert>
        ))}
      {uploadState === "complete" &&
        (featureExtractCount > 0 ? (
          <Alert>
            {" "}
            {featureExtractCount} feature(s) extracted successfully
          </Alert>
        ) : (
          <Alert
            sx={{
              marginTop: "16px",
            }}
            icon={<Warning />}
            color="error"
          >
            No features could be extracted
          </Alert>
        ))}
      {uploadState === "complete" && (
        <Button
          sx={{
            marginTop: "16px",
          }}
          onClick={() => {
            setUploadState("ready");
          }}
        >
          Upload again
        </Button>
      )}
    </Box>
  );
};
